import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, Gauge, MousePointer2, Smartphone, TrendingUp } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { HeroImage } from '@/components/site/HeroImage';
import { HERO_IMAGES } from '@/data/heroImages';
import { SITE, SERVICES } from '@/data/site';
import { PageLinks } from '@/components/site/ServicePageParts';
import { CITIES } from '@/data/cities';

gsap.registerPlugin(ScrollTrigger);

const PROBLEMS = [
  {
    icon: Gauge,
    title: 'Slow load times',
    body: 'A 1-second delay drops conversions by ~7%. We ship sites that paint in under a second on a midrange Indian phone.',
  },
  {
    icon: MousePointer2,
    title: 'Outdated design',
    body: 'A 2017 theme says “we stopped caring” louder than any tagline. We design for the way browsers, attention and search look today.',
  },
  {
    icon: Smartphone,
    title: 'Poor mobile experience',
    body: 'Most of your visitors are on a phone in a metro queue. We design mobile-first, then scale up—never the other way around.',
  },
  {
    icon: TrendingUp,
    title: 'Low conversion rates',
    body: 'Pretty without persuasive is decoration. Every section earns its place against one of three goals: trust, clarity, or action.',
  },
];

const WHAT_WE_BUILD = [
  { tag: 'Brand · Trust', title: 'Corporate sites', body: 'Multi-page sites for established companies—leadership, capabilities, investor narrative, careers.', accent: '#6C5CE7' },
  { tag: 'Personal · Craft', title: 'Portfolio sites', body: 'Editorial portfolios for designers, studios and creators. Slow scroll, real typography, no template.', accent: '#4B9FFF' },
  { tag: 'Fundraise · Velocity', title: 'Startup sites', body: 'Product-narrative sites for pre-seed to Series B. Built to ship fast and re-tell the story every quarter.', accent: '#6C5CE7' },
  { tag: 'Performance · Paid', title: 'Landing pages', body: 'High-intent landing pages for ad campaigns and launches. A/B-ready, conversion-instrumented, fast.', accent: '#4B9FFF' },
  { tag: 'Local · Booking', title: 'Service-business sites', body: 'For clinics, studios, agencies, hospitality. Phone-first, WhatsApp-CTA, locale-aware copy.', accent: '#4B9FFF' },
  { tag: 'Editorial · SEO', title: 'Content platforms', body: 'Blogs and resource hubs with proper IA, RSS, OG, and a CMS your marketing team will actually use.', accent: '#6C5CE7' },
];

const PROCESS = [
  { step: '01', title: 'Research', body: 'Stakeholder interviews, competitor teardown, analytics audit. Two weeks of listening before a single rectangle.' },
  { step: '02', title: 'Wireframes', body: 'Low-fi flows in the browser. We argue about hierarchy here, not in pixel-perfect mocks.' },
  { step: '03', title: 'UI design', body: 'One source-of-truth Figma file. Tokens, components, motion notes, real copy.' },
  { step: '04', title: 'Development', body: 'Engineering picks up the file, not a hand-off PDF. React / Next / Astro depending on the brief. CMS wired in week one.' },
  { step: '05', title: 'Testing', body: 'Lighthouse, real-device QA, axe accessibility, content QA with your team. Then we ship—and stay for the first month.' },
];

const TECH = [
  'Next.js', 'React', 'Astro', 'TypeScript', 'Tailwind', 'Framer Motion', 'GSAP', 'Sanity', 'Contentful',
  'Strapi', 'Vercel', 'Cloudflare', 'Supabase', 'Algolia', 'Stripe', 'Razorpay', 'Plausible', 'PostHog',
];

const OUTCOMES = [
  { kpi: 98, suffix: '/100', label: 'avg. Lighthouse mobile' },
  { kpi: 42, prefix: '+', suffix: '%', label: 'median conversion lift' },
  { kpi: 3.6, prefix: '×', suffix: '', label: 'mobile session duration' },
  { kpi: 0.9, suffix: 's', label: 'avg. LCP on 4G mid-tier' },
];

const FAQ = [
  { q: 'How long does a website project take?', a: 'A focused marketing site is 6–10 weeks end-to-end. A multi-section corporate site or a heavy content platform runs 10–14 weeks. Landing pages can ship in 2–3 weeks.' },
  { q: 'Do you write the copy or do we?', a: 'Either works. We have a writer on the bench for in-house copy at additional scope. For most engagements your team writes the rough draft and we edit for hierarchy, rhythm and SEO.' },
  { q: 'Which CMS do you recommend?', a: 'Sanity for content-heavy marketing teams, Contentful when there is enterprise IT, Strapi when data must stay self-hosted. We will recommend in week one—not before.' },
  { q: 'Will I be able to edit the site myself?', a: 'Yes. Everything that should be editable is editable. We train your team on day of launch and hand over a Loom + a Notion runbook.' },
  { q: 'Do you handle SEO and analytics?', a: 'We ship a technical SEO foundation (semantic HTML, sitemap, OG, schema, Core Web Vitals) and an analytics layer (Plausible or GA4 + PostHog). Content SEO is a separate engagement.' },
  { q: 'What about hosting?', a: 'Vercel or Cloudflare Pages by default. We can also deploy to your AWS / GCP account. We do not host on a shared box.' },
];

/* --- Counter component --- */
function Counter({ value, prefix = '', suffix = '', decimals = 0 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setVal(value); return; }
    const node = ref.current;
    if (!node) return;
    let started = false;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started) {
        started = true;
        const obj = { v: 0 };
        gsap.to(obj, {
          v: value,
          duration: 1.6,
          ease: 'power2.out',
          onUpdate: () => setVal(obj.v),
        });
        obs.disconnect();
      }
    }, { threshold: 0.4 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [value]);
  const display = decimals > 0 || (!Number.isInteger(value))
    ? val.toFixed(1)
    : Math.round(val).toString();
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}{display}{suffix}
    </span>
  );
}

/* --- Before / After comparison slider --- */
function BeforeAfter() {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setPos(p);
    };
    const onUp = () => { dragging.current = false; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const startDrag = (e) => {
    dragging.current = true;
    document.body.style.userSelect = 'none';
    // also accept the initial click position
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const p = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setPos(p);
    }
  };

  // Keyboard support
  const onKey = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 2));
    if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 2));
  };

  return (
    <div
      ref={wrapRef}
      data-testid="before-after-slider"
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      className="relative aspect-[16/10] w-full rounded-[20px] overflow-hidden border border-white/[0.12] bg-ink-surface select-none cursor-ew-resize"
    >
      {/* AFTER (full layer behind) */}
      <MockSite variant="after" />

      {/* BEFORE (clipped) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <MockSite variant="before" />
      </div>

      {/* Labels */}
      <div className="pointer-events-none absolute top-4 left-4 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
        Before
      </div>
      <div className="pointer-events-none absolute top-4 right-4 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1.5">
        After
      </div>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/80"
        style={{ left: `${pos}%` }}
      >
        <button
          type="button"
          data-testid="before-after-handle"
          aria-label="Drag to compare"
          tabIndex={0}
          onKeyDown={onKey}
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white text-[#0A0A0F] shadow-[0_8px_30px_-8px_rgba(0,0,0,0.6)] flex items-center justify-center hover:scale-105 transition-transform"
        >
          <span className="-mr-0.5">‹</span>
          <span>›</span>
        </button>
      </div>

      {/* progress hint */}
      <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-white/60">
        Drag to compare
      </div>
    </div>
  );
}

/* Two CSS-only "mock" site frames so the slider is interactive without external assets. */
function MockSite({ variant }) {
  if (variant === 'before') {
    return (
      <div className="absolute inset-0 bg-[#F4F1E8] text-[#1a1a1a]">
        {/* Old-school heavy header */}
        <div className="h-14 border-b border-black/10 px-6 flex items-center justify-between bg-[#E9E4D2]">
          <div className="text-[11px] tracking-widest uppercase">Saraya Bio Solutions Pvt. Ltd.</div>
          <div className="hidden md:flex gap-4 text-[10px] uppercase tracking-widest text-black/70">
            <span>Home</span><span>About Us</span><span>Products</span><span>Services</span><span>Contact</span>
          </div>
        </div>
        <div className="p-6 md:p-10 grid grid-cols-2 gap-6 h-[calc(100%-3.5rem)]">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-orange-700">Welcome to our website</div>
            <div className="mt-3 font-serif text-2xl md:text-4xl leading-[1.15]">
              Leading provider of <span className="text-orange-700">solutions</span> for your business needs since 1998
            </div>
            <p className="mt-3 text-[11px] md:text-[13px] text-black/70 leading-relaxed">
              We are committed to delivering quality products and services to our esteemed clientele across India and abroad.
            </p>
            <button className="mt-4 px-4 py-2 bg-orange-700 text-white text-[11px] uppercase tracking-widest">
              Click Here ▸
            </button>
          </div>
          <div className="bg-[length:14px_14px] bg-[linear-gradient(135deg,rgba(0,0,0,0.06)_25%,transparent_25%,transparent_50%,rgba(0,0,0,0.06)_50%,rgba(0,0,0,0.06)_75%,transparent_75%)] border border-black/10 flex items-center justify-center">
            <div className="text-black/40 text-xs uppercase tracking-widest">[ stock image ]</div>
          </div>
        </div>
      </div>
    );
  }
  // After — Ink & Electric style
  return (
    <div className="absolute inset-0 bg-[#0A0A0F] text-white overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div
        className="absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(108,92,231,0.35)' }}
      />
      <div className="h-14 px-6 flex items-center justify-between border-b border-white/[0.08] bg-black/40 backdrop-blur">
        <div className="flex items-center gap-2 text-[12px] font-semibold tracking-tight">
          <svg viewBox="0 0 32 32" className="h-5 w-5"><path d="M16 5 L28 26 L4 26 Z" fill="none" stroke="#6C5CE7" strokeWidth="2"/></svg>
          Saraya<span className="text-white/50">Bio</span>
        </div>
        <div className="hidden md:flex items-center gap-5 text-[11px] text-white/70">
          <span>Science</span><span>Products</span><span>Press</span><span>Careers</span>
          <span className="px-3 py-1.5 rounded-full bg-violet text-white text-[11px]">Talk to us →</span>
        </div>
      </div>
      <div className="p-6 md:p-10 h-[calc(100%-3.5rem)] grid grid-cols-12 gap-6">
        <div className="col-span-7">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">— Since 1998 · Pune</div>
          <div className="mt-4 text-2xl md:text-4xl font-extrabold tracking-tight leading-[1.05]">
            Bioscience that <span className="italic text-violet">earns</span> the lab and the boardroom.
          </div>
          <div className="mt-4 text-[12px] text-white/70 leading-relaxed max-w-md">
            Reagents, assays and contract research for India's biotech operators. 27 years of compounding precision—now with a digital backbone.
          </div>
          <div className="mt-5 flex gap-2">
            <span className="px-3 py-2 rounded-full bg-violet text-[11px]">Book a lab tour ↗</span>
            <span className="px-3 py-2 rounded-full border border-white/15 text-[11px]">Catalog</span>
          </div>
        </div>
        <div className="col-span-5 grid grid-rows-3 gap-3">
          {[
            { k: '98', s: '/100', l: 'Lighthouse mobile' },
            { k: '+42', s: '%', l: 'demo requests' },
            { k: '0.9', s: 's', l: 'LCP on 4G' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl border border-white/[0.12] bg-white/[0.02] px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-xl font-bold tracking-tight">{s.k}<span className="text-violet">{s.s}</span></div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">{s.l}</div>
              </div>
              <div className="h-8 w-8 rounded-md border border-white/15" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- Page --- */
export default function WebsiteDevelopment() {
  const techRef = useRef(null);

  // signature scroll-linked accent under the hero word
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !techRef.current) return;
    const ctx = gsap.context(() => {
      gsap.to('.tech-marquee-inner', {
        xPercent: -50,
        ease: 'none',
        duration: 28,
        repeat: -1,
      });
    }, techRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="website-development-page">
      <FaqSchema items={FAQ} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.3} />
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-20 md:pb-28">
          {/* Breadcrumb */}
          <nav
            data-testid="breadcrumb"
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-ink-muted"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link to="/services/" className="hover:text-white transition-colors">Services</Link>
            <ChevronRight size={12} />
            <span className="text-white">Website Development</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-x-10 md:gap-y-16">
            <div className="md:col-span-9">
              <SectionLabel as="h1" num="01" data-testid="wd-eyebrow" className="mt-2">
                {SERVICES[0].h1}
              </SectionLabel>
              <WordReveal
                as="p"
                text="Websites that turn visitors into customers."
                accent="customers."
                data-testid="wd-headline"
                className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[78px] font-extrabold tracking-tightest leading-[1.02] text-balance"
              />
            </div>
            <div className="md:col-span-7 md:col-start-6 md:row-start-2">
              <Reveal delay={400}>
                <p className="text-lg md:text-xl text-ink-muted leading-relaxed max-w-xl">
                  We build marketing sites that earn their place in your funnel. Mobile-first, sub-second,
                  measured against three numbers you pick on day one—not against a Dribbble shot.
                </p>
              </Reveal>
              <Reveal delay={520}>
                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <MagneticButton
                    href={SITE.whatsapp}
                    external
                    variant="primary"
                    data-testid="wd-hero-cta-primary"
                  >
                    Book discovery call
                    <ArrowUpRight size={16} />
                  </MagneticButton>
                  <MagneticButton to="/work/" variant="ghost" data-testid="wd-hero-cta-secondary">
                    See website work
                  </MagneticButton>
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-5 md:col-start-1 md:row-start-2 self-end" data-testid="wd-hero-image">
              <HeroImage
                src={HERO_IMAGES['website-development'].src}
                alt={HERO_IMAGES['website-development'].alt}
                testId="wd-hero-image-frame"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS WE SOLVE */}
      <section data-testid="wd-problems" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="02">Problems we solve</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] text-balance">
              The four reasons your site <span className="italic text-violet">isn’t pulling its weight.</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
              We&apos;ve diagnosed all four enough times to know the patterns by week-one. We don&apos;t fix all of them on every project—we fix the ones the numbers tell us to.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PROBLEMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 80}>
                  <div
                    data-testid={`wd-problem-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="group h-full rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 md:p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(108,92,231,0.5)]"
                  >
                    <div className="h-10 w-10 rounded-xl border border-white/[0.12] flex items-center justify-center text-violet bg-white/[0.02]">
                      <Icon size={18} />
                    </div>
                    <div className="mt-5 text-xl font-semibold tracking-tight">{p.title}</div>
                    <p className="mt-3 text-ink-muted leading-relaxed text-sm">{p.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD */}
      <section data-testid="wd-what-we-build" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <SectionLabel num="03">What we build</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Six shapes of <span className="italic text-electric">marketing site.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-lg">
                  Different goals deserve different shapes. We start with what the site has to do—then choose
                  the IA, the page model, the CMS and the motion language around that.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {WHAT_WE_BUILD.map((w, i) => {
              // Asymmetric: 7/5, 5/7, 6/6
              const spans = ['md:col-span-7', 'md:col-span-5', 'md:col-span-5', 'md:col-span-7', 'md:col-span-6', 'md:col-span-6'];
              return (
                <Reveal key={w.title} delay={i * 70} className={spans[i]}>
                  <div
                    data-testid={`wd-build-${w.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="group relative h-full overflow-hidden rounded-[16px] border border-white/[0.12] bg-ink-bg p-6 md:p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(75,159,255,0.45)]"
                  >
                    <div
                      className="absolute -right-16 -top-16 h-44 w-44 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-700"
                      style={{ background: w.accent }}
                    />
                    <div className="relative flex h-full flex-col">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-ink-muted">{w.tag}</div>
                      <div className="mt-4 flex items-baseline justify-between">
                        <div className="text-2xl md:text-3xl font-bold tracking-tight">{w.title}</div>
                        <ArrowUpRight size={18} className="arrow-link arrow text-ink-muted group-hover:text-white transition-colors" />
                      </div>
                      <p className="mt-4 text-ink-muted leading-relaxed text-sm max-w-md">{w.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DESIGN APPROACH */}
      <section data-testid="wd-approach" className="relative py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="04">Our approach</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Five stages. <span className="italic text-violet">One file.</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
              Research informs wireframes. Wireframes inform UI. UI is built by the engineer who joined in week one. Testing is a stage—not a vibe check.
            </p>
          </div>
          <div className="md:col-span-8 space-y-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 70}>
                <div
                  data-testid={`wd-process-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group rounded-[16px] border border-white/[0.12] bg-ink-surface p-6 md:p-7 transition-all duration-500 hover:border-white/25 hover:-translate-y-0.5"
                >
                  <div className="flex items-baseline gap-6">
                    <div className="text-violet tabular-nums text-sm">{p.step}</div>
                    <div className="h-px w-12 bg-white/15" />
                    <div className="text-xl md:text-2xl font-semibold tracking-tight">{p.title}</div>
                  </div>
                  <p className="mt-4 ml-[68px] text-ink-muted leading-relaxed max-w-2xl">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGIES — marquee */}
      <section
        ref={techRef}
        data-testid="wd-tech"
        className="relative py-24 md:py-28 border-y border-white/10 bg-ink-surface/30 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionLabel num="05">Technologies</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-2xl text-balance">
              A stack we&apos;d <span className="italic text-electric">defend</span> in a code review.
            </h2>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <p className="text-ink-muted leading-relaxed max-w-md">
              We aren&apos;t precious about tools—we are precious about ship-day behaviour. Each of these is here because it makes a site faster, cheaper to run, or easier for your team to live with.
            </p>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-r from-ink-bg to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-l from-ink-bg to-transparent pointer-events-none" />
          <div className="flex whitespace-nowrap tech-marquee-inner gap-6 will-change-transform">
            {[...TECH, ...TECH].map((t, i) => (
              <div
                key={i}
                className="shrink-0 inline-flex items-center gap-3 px-6 py-3.5 rounded-full border border-white/[0.12] bg-ink-bg text-sm tracking-tight"
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: i % 2 === 0 ? '#6C5CE7' : '#4B9FFF' }} />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT — BEFORE / AFTER */}
      <section data-testid="wd-before-after" className="relative py-24 md:py-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <SectionLabel num="06">Illustration · Before / After</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
                A typical rebuild — <span className="italic text-violet">drag the handle.</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pl-6 md:border-l border-white/10">
              <Reveal>
                <p className="text-ink-muted leading-relaxed max-w-md">
                  An illustrative example, not a client site: an established B2B firm whose website has not kept up. A rebuild like this
                  is planned around three numbers: enquiries, mobile bounce and Core Web Vitals.
                </p>
              </Reveal>
              
            </div>
          </div>
          <Reveal>
            <BeforeAfter />
          </Reveal>
        </div>
      </section>

      {/* OUTCOMES */}
      <section data-testid="wd-outcomes" className="relative py-24 md:py-32 bg-ink-surface/30 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <SectionLabel num="07">Outcomes</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                Numbers we&apos;d sign <span className="italic text-electric">a contract</span> against.
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <Reveal>
                <p className="text-ink-muted leading-relaxed">
                  Medians across the last 12 website engagements (2024–2025). Individual results vary—what doesn&apos;t vary is that we measure and publish them.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.label} delay={i * 90}>
                <div
                  data-testid={`wd-outcome-${i}`}
                  className="border-l border-white/[0.12] pl-5"
                >
                  <div className="text-5xl md:text-6xl font-extrabold tracking-tight leading-none">
                    <Counter
                      value={o.kpi}
                      prefix={o.prefix}
                      suffix={o.suffix}
                      decimals={!Number.isInteger(o.kpi) ? 1 : 0}
                    />
                  </div>
                  <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                    {o.label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="wd-faq" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="08">FAQ</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Things we&apos;re asked <span className="italic text-violet">before</span> the call.
            </h2>
          </div>
          <div className="md:col-span-8 divide-y divide-white/10 border-y border-white/10">
            {FAQ.map((f, i) => (
              <FAQItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      <PageLinks
        heading="Website development by city"
        intro="One studio, working across Maharashtra. Each city page covers local industries, areas, costs and questions."
        links={[
          ...CITIES.map((c) => ({ to: `${c.path}/`, label: `Website development in ${c.name}` })),
          { to: '/website-development-cost/', label: 'Website development cost' },
          { to: '/services/shopify-development/', label: 'Shopify development' },
          { to: '/services/website-maintenance/', label: 'Website maintenance and redesign' },
          { to: '/work/rupantaram/', label: 'Rupantaram website case study' },
        ]}
        testIdPrefix="wd"
      />

      {/* RELATED SERVICES */}
      <section data-testid="wd-related" className="relative py-24 md:py-32 border-t border-white/10 bg-ink-surface/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <SectionLabel num="09">Related services</SectionLabel>
              <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                If this fits, these <span className="italic text-electric">might too.</span>
              </h2>
            </div>
            <Link to="/services/" className="arrow-link hidden md:inline-flex items-center gap-2 text-sm text-white">
              All services <span className="arrow">→</span>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SERVICES
              .filter((s) => ['web-app-development', 'ecommerce-website-development', 'ai-solutions'].includes(s.slug))
              .map((s, i) => (
                <Reveal key={s.slug} delay={i * 80}>
                  <Link
                    to={`/services/#${s.slug}`}
                    data-testid={`wd-related-${s.slug}`}
                    className="group block h-full rounded-[16px] border border-white/[0.12] bg-ink-bg p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-white/25 hover:shadow-[0_20px_60px_-30px_rgba(108,92,231,0.45)]"
                  >
                    <div className="flex items-baseline justify-between">
                      <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">{s.num} / 06</div>
                      <ArrowUpRight size={16} className="text-ink-muted group-hover:text-white transition-colors" />
                    </div>
                    <div className="mt-6 text-2xl font-bold tracking-tight">{s.title}</div>
                    <p className="mt-3 text-ink-muted leading-relaxed text-sm">{s.short}</p>
                  </Link>
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Ready to redesign?"
        headline="Let’s ship a site you’ll actually link to."
        sub="A 30-minute WhatsApp call with a partner—no SDR, no deck. Bring the URL of the site you wish yours behaved like."
      />
    </div>
  );
}

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="py-2">
      <button
        type="button"
        data-testid={`wd-faq-${index}`}
        onClick={() => setOpen((s) => !s)}
        className="w-full text-left py-5 grid grid-cols-12 gap-4 items-baseline group"
      >
        <div className="col-span-1 text-xs uppercase tracking-[0.22em] text-ink-muted tabular-nums">
          0{index + 1}
        </div>
        <div className="col-span-9 md:col-span-10 text-lg md:text-xl font-semibold tracking-tight group-hover:text-white">
          {q}
        </div>
        <div className="col-span-2 md:col-span-1 text-right text-ink-muted">
          <span className={`inline-block transition-transform duration-300 ${open ? 'rotate-45' : ''}`}>＋</span>
        </div>
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-500 ease-out"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="grid grid-cols-12 gap-4 pb-6">
            <div className="col-span-1" />
            <p className="col-span-11 text-ink-muted leading-relaxed max-w-2xl">{a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
